import { useDispatch } from "react-redux";
import Xarrow from "react-xarrows";
import { importFromYaml } from "../../../../abstract/controller/modelYamlImport";
import * as tutorialUtils from "../../../../abstract/controller/utils/tutorialUtils";
import { useArrowSync } from "../../../../abstract/hook/useArrowSync";
import { setRoot } from "../../../../abstract/redux/slice/structureSlice";
import ButtonIntroWide from "../../../ui/buttons/ButtonIntroWide";
import ButtonTomatoClose from "../../../ui/buttons/ButtonTomatoClose";
import styles from "./Manual.module.scss";

const id = "logic3";
const idNext = "logic4";
const idPrev = "logic3";

const next = `
global parameters:
  - parameter: weapon
    choices:
      - choice: sword
      - choice: dagger
      - choice: axe
      - choice: mace
      - choice: spear
functions:
  - function: simple
    parameters:
      - parameter: hero
        choices:
          - choice: Peter
          - choice: Susan
          - choice: Edmund
          - choice: Lucy
      - linked parameter: hero weapon
        linked to: weapon
      - parameter: foe
        choices:
          - choice: witch
          - choice: cyclops
          - choice: giant
      - linked parameter: foe weapon
        linked to: weapon
      - parameter: location
        choices:
          - choice: forest
          - choice: castle
          - choice: river
          - choice: mountain
          - choice: lake
    logic:
      - constraint: Giants use short weapons
        expression: "'foe' IS 'giant' => 'foe weapon' IN ['dagger', 'axe', 'mace']"
      - alias: Girls
        expression: "'hero' IN ['Susan', 'Lucy']"
      - constraint: Girls use daggers
        expression: IF 'Girls' THEN 'hero weapon' IS 'dagger'
`;

const prev = `
global parameters:
  - parameter: weapon
    choices:
      - choice: sword
      - choice: dagger
      - choice: axe
      - choice: mace
      - choice: spear
functions:
  - function: simple
    parameters:
      - parameter: hero
        choices:
          - choice: Peter
          - choice: Susan
          - choice: Edmund
          - choice: Lucy
      - linked parameter: hero weapon
        linked to: weapon
      - parameter: foe
        choices:
          - choice: witch
          - choice: cyclops
          - choice: giant
      - linked parameter: foe weapon
        linked to: weapon
      - parameter: location
        choices:
          - choice: forest
          - choice: castle
          - choice: river
          - choice: mountain
          - choice: lake
    logic:
      - constraint: Giants use short weapons
        expression: "'foe' IS 'giant' => 'foe weapon' IN ['dagger', 'axe', 'mace']"
`;

function ManualLogic3({ tutorial, setTutorial }) {
  if (tutorial === id) {
    return <Core setTutorial={setTutorial} />;
  }

  return null;
}

function Core({ setTutorial }) {
  const _dispatch = useDispatch();
  const renderTrigger = useArrowSync();

  const handleShowMe = () => {
    const { model } = importFromYaml(next);
    _dispatch(setRoot(model, 0));
  };

  const handleBack = () => {
    const { model } = importFromYaml(prev);
    _dispatch(setRoot(model, 0));
    setTutorial(idPrev);
  };

  const handleClose = () => {
    setTutorial("null");
  };

  return (
    <>
      {document.getElementById("header_logic") && !document.getElementById("logic_constraint") && (
        <div className={`${styles["container"]} ${styles["container-l3a"]} ${styles["overlay"]}`}>
          <div id="tutorial-text-gp1a" className={`${styles["container__text"]}`}>
            <p>To show/hide logic click on the header.</p>
          </div>

          <Xarrow
            key={`arrow-1-${renderTrigger}`}
            start="tutorial-text-gp1a"
            end="header_logic"
            color="#415B32"
            strokeWidth={2}
            path="smooth"
            curveness={0.8}
            headSize={6}
          />
        </div>
      )}

      <div className={`${styles["foreground"]} ${styles["foreground-l3"]}  ${styles["overlay"]}`}>
        <ButtonTomatoClose onClick={handleClose} />
        <div className={styles["header"]}>
          <div className={styles["header__assistant"]}></div>
          <div className={styles["header__text--main"]}>
            <p>
              Now, let's use the alias. Create a new constraint and name it <b>Girls use daggers</b>
              . The expression should be: <b>IF 'Girls' THEN 'hero weapon' IS 'dagger'</b>.
            </p>
          </div>
        </div>
        <div className={styles["confirmation"]}>
          <div className={styles["button"]}>
            <ButtonIntroWide theme="red" title="Go back!" onClick={handleBack} />
          </div>
          <div className={styles["button"]}>
            <ButtonIntroWide theme="tomato" title="Show me!" onClick={handleShowMe} />
          </div>
        </div>
      </div>
    </>
  );
}

export const manualValidateLogic3 = (root, tutorial, setTutorial, setMessage) => {
  if (tutorial === id) {
    let { res: resFun, msg: msgFun } = tutorialUtils.validateSingleFunction(root);

    if (!resFun) {
      setMessage(msgFun);
      return false;
    }

    let { res: resParam, msg: msgParam } = tutorialUtils.validateParameters(
      root,
      "root→[function]→simple",
      ["hero weapon", "foe weapon", "hero", "foe", "location"],
    );

    if (!resParam) {
      setMessage(msgParam);
      return false;
    }

    let { res: resConA, msg: msgConA } = tutorialUtils.validateCondition(
      root,
      "root→[function]→simple→[logic]→Giants use short weapons",
      "'foe' IS 'giant' => 'foe weapon' IN ['dagger', 'axe', 'mace']",
    );

    if (!resConA) {
      setMessage(msgConA);
      return false;
    }

    let { res: resConB, msg: msgConB } = tutorialUtils.validateCondition(
      root,
      "root→[function]→simple→[logic]→Girls use daggers",
      "IF 'Girls' THEN 'hero weapon' IS 'dagger'",
    );

    if (!resConB) {
      setMessage(msgConB);
      return false;
    }

    let { res: resAlias, msg: msgAlias } = tutorialUtils.validateCondition(
      root,
      "root→[function]→simple→[logic]→Girls",
      "'hero' IN ['Susan', 'Lucy']",
    );

    if (!resAlias) {
      setMessage(msgAlias);
      return false;
    }

    setTutorial(idNext);

    setMessage("");
    return true;
  }

  return false;
};

export default ManualLogic3;
